/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SelectDispatchLocation.tsx
 🗓️ Created: 2026-03-20
 🧩 Description:
    Searchable select component for Dispatch Locations (E911).
    Used in Extension and Hot Desking forms.
    Uses ReactDOM.createPortal to avoid overflow:hidden clipping.
────────────────────────────────────────────────────────────────*/

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Check, MapPin } from 'lucide-react';

export interface DispatchLocationOption {
  id: number;
  locationName: string;
  formattedAddress?: string;
  elin?: string;
}

interface SelectDispatchLocationProps {
  id?: string;
  value: number | null;
  onChange: (value: number | null) => void;
  locations: DispatchLocationOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
}

export default function SelectDispatchLocation({
  id,
  value,
  onChange,
  locations,
  placeholder = 'Select E911 Location',
  disabled = false,
  className = '',
  error = false,
}: SelectDispatchLocationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLocation = value ? locations.find(l => l.id === value) : null;

  const filteredLocations = locations.filter(loc =>
    loc.locationName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (loc.formattedAddress && loc.formattedAddress.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (loc.elin && loc.elin.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Calculate dropdown position based on trigger element
  const updateDropdownPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = Math.min(300, filteredLocations.length * 44 + 80);
    const openUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus search on open, update position on scroll/resize
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      if (inputRef.current) inputRef.current.focus();

      const handleScroll = () => updateDropdownPosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [isOpen]);

  const handleSelect = (id: number | null) => {
    onChange(id);
    setIsOpen(false);
    setSearchQuery('');
  };

  const dropdown = isOpen ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="bg-popover border border-border rounded-md shadow-xl"
    >
      {/* Search Input */}
      <div className="p-2 border-b border-border">
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, address or ELIN..."
          className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {/* Options */}
      <div className="max-h-60 overflow-y-auto p-1">
        {/* Clear selection */}
        <button
          type="button"
          onClick={() => handleSelect(null)}
          className={`
            w-full px-3 py-2 text-left rounded-md text-sm
            hover:bg-accent hover:text-accent-foreground
            flex items-center justify-between
            transition-colors
            ${!value ? 'bg-accent text-accent-foreground' : ''}
          `}
        >
          <span className="text-muted-foreground italic">None</span>
          {!value && <Check className="h-4 w-4 text-primary" />}
        </button>

        {filteredLocations.length > 0 ? (
          filteredLocations.map((loc) => (
            <button
              key={loc.id}
              type="button"
              onClick={() => handleSelect(loc.id)}
              className={`
                w-full px-3 py-2 text-left rounded-md text-sm
                hover:bg-accent hover:text-accent-foreground
                flex items-center justify-between gap-2
                transition-colors
                ${value === loc.id ? 'bg-accent text-accent-foreground' : ''}
              `}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{loc.locationName}</div>
                {loc.formattedAddress && (
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">
                    {loc.formattedAddress}
                  </div>
                )}
                {loc.elin && (
                  <div className="text-xs text-blue-500 mt-0.5">ELIN: {loc.elin}</div>
                )}
              </div>
              {value === loc.id && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
            </button>
          ))
        ) : (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            No locations found
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        id={id}
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left border rounded-md text-sm
          bg-background text-foreground
          ${error ? 'border-red-500' : 'border-input'}
          hover:bg-accent hover:text-accent-foreground
          focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-between gap-2
          transition-colors
        `}
      >
        <span className={`flex items-center gap-2 ${selectedLocation ? '' : 'text-muted-foreground'}`}>
          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
          {selectedLocation ? selectedLocation.locationName : placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown rendered via Portal to escape overflow:hidden parents */}
      {typeof document !== 'undefined' && ReactDOM.createPortal(dropdown, document.body)}
    </div>
  );
}
